import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Trophy, Search, CheckCircle, XCircle, AlertCircle, Loader2, RefreshCw, Calendar, Printer } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';
import { useAuth } from '../../../app/auth';
import { fechaLocalYMD, parseBackendDate, formatHoraCR } from '../../../utils/date';
import { printComprobantePremioByDestination } from '../../../utils/printer/printRouter';

// ── Types ──────────────────────────────────────────────────────────────

interface NumeroGanadorHoy {
  id: number;
  sorteo_id: string;
  sorteo_nombre: string;
  tipo_id: string;
  fecha: string;
  hora_cierre: string;
  num1: string;
  num2: string | null;
  num3: string | null;
  tipo_premio: string | null;
  tipo_monazos: string | null;
  paga_reventado: boolean | null;
  verificado: boolean;
  total_pagado_local?: number | null;
}

interface ConsultaTicketResponse {
  ticket_id: string;
  ticket_number: string;
  sorteo_nombre: string;
  estado: string;
  total: string;
  premio_calculado: string;
  vencido: boolean;
  ya_premiado: boolean;
  anulado: boolean;
  jugadas_ganadoras: Array<{
    num1: string;
    num2: string | null;
    monto_apostado: string;
    multiplicador: string;
    premio: string;
    concepto: string;
  }>;
}

interface PagarResponse {
  id: string;
  monto: string;
  ticket_number: string;
  created_at?: string;
}

interface ComprobantePago {
  ticketNumber: string;
  sorteoNombre: string;
  monto: number;
  pagadoPor: string;
  fechaPago: string;
}

// ── Helpers ────────────────────────────────────────────────────────────

function formatNumGanador(g: NumeroGanadorHoy): string {
  const p = (s: string, n = 2) => s.padStart(n, '0');
  switch (g.tipo_id) {
    case 'NORMAL':    return p(g.num1);
    case 'REVENTADO': return g.tipo_premio ? `${p(g.num1)} (${g.tipo_premio})` : p(g.num1);
    case 'MEGA':      return g.num2 ? `${p(g.num1)} / ${p(g.num2)}` : p(g.num1);
    case 'MONAZOS':   return g.tipo_monazos ? `${p(g.num1, 3)} (${g.tipo_monazos})` : p(g.num1, 3);
    case 'FECHAS':    return g.num2 ? `${p(g.num1)}-${p(g.num2)}` : p(g.num1);
    case 'PARLEY': {
      const partes = [g.num1];
      if (g.num2) partes.push(g.num2);
      if (g.num3) partes.push(g.num3);
      return partes.map(s => p(s)).join(' + ');
    }
    default: return g.num1;
  }
}

// ── Component ──────────────────────────────────────────────────────────

export const VendedorPremiosPage: React.FC = () => {
  const { user } = useAuth();
  const localId = user?.localId ?? '';
  const hoy = fechaLocalYMD();

  // Ganadores
  const [fechaGanadores, setFechaGanadores] = useState(hoy);
  const [ganadores, setGanadores] = useState<NumeroGanadorHoy[]>([]);
  const [loadingGanadores, setLoadingGanadores] = useState(false);

  // Búsqueda de ticket
  const [barcode, setBarcode] = useState('');
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [consulta, setConsulta] = useState<ConsultaTicketResponse | null>(null);
  const [error, setError] = useState('');

  // Comprobante del último pago
  const [comprobante, setComprobante] = useState<ComprobantePago | null>(null);
  const [imprimiendo, setImprimiendo] = useState(false);

  // Historial — removed: propios no ven lista de tickets premiados

  const inputRef = useRef<HTMLInputElement>(null);

  const cargarGanadores = useCallback(async () => {
    setLoadingGanadores(true);
    try {
      const data = await fetchAPI<NumeroGanadorHoy[]>(`/ganadores?fecha=${fechaGanadores}`);
      setGanadores(data.filter(g => g.verificado));
    } catch {
      setGanadores([]);
    } finally {
      setLoadingGanadores(false);
    }
  }, [fechaGanadores]);

  const cargarHistorial = useCallback(async () => {}, []);

  useEffect(() => {
    cargarGanadores();
    cargarHistorial();
  }, [cargarGanadores, cargarHistorial]);

  const consultar = useCallback(async (bc: string) => {
    const codigo = bc.trim().toUpperCase();
    if (!codigo) return;
    setLoading(true);
    setError('');
    setConsulta(null);
    try {
      const data = await fetchAPI<ConsultaTicketResponse>(
        `/premios/consultar/${encodeURIComponent(codigo)}?local_id=${encodeURIComponent(localId)}`
      );
      setConsulta(data);
    } catch (e: any) {
      setError(e.message || 'No se pudo consultar el ticket');
    } finally {
      setLoading(false);
    }
  }, [localId]);

  const pagar = useCallback(async () => {
    if (!consulta) return;
    setPaying(true);
    setError('');
    try {
      const resp = await fetchAPI<PagarResponse>('/premios/validar', {
        method: 'POST',
        body: JSON.stringify({ barcode: consulta.ticket_number, local_id: localId }),
      });

      const u = user as any;
      const comp: ComprobantePago = {
        ticketNumber: resp.ticket_number ?? consulta.ticket_number,
        sorteoNombre: consulta.sorteo_nombre,
        monto: parseFloat(resp.monto),
        pagadoPor: u?.nombre ?? u?.username ?? u?.email ?? 'Usuario',
        fechaPago: resp.created_at ?? new Date().toISOString(),
      };
      setComprobante(comp);

      // Imprimir automáticamente
      printComprobantePremioByDestination(comp).catch(() => {});

      setConsulta(null);
      setBarcode('');
      await cargarHistorial();
      setTimeout(() => inputRef.current?.focus(), 50);
    } catch (e: any) {
      setError(e.message || 'No se pudo pagar el premio');
    } finally {
      setPaying(false);
    }
  }, [consulta, localId, cargarHistorial, user]);

  const reimprimir = useCallback(() => {
    if (!comprobante) return;
    setImprimiendo(true);
    printComprobantePremioByDestination(comprobante)
      .finally(() => setImprimiendo(false));
  }, [comprobante]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    consultar(barcode);
  };

  const premioNum = parseFloat(consulta?.premio_calculado ?? '0');
  const esGanador = !consulta?.vencido && !consulta?.ya_premiado && !consulta?.anulado && premioNum > 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Validacion de Premios</h1>
          <p className="text-gray-500 text-sm">Escanea el codigo de barras del ticket</p>
        </div>
        <button
          onClick={() => cargarGanadores()}
          className="text-gray-400 hover:text-gray-600 p-1"
          title="Actualizar"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Números Ganadores */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="flex items-center gap-2 font-semibold text-gray-800">
            <Trophy size={18} className="text-yellow-500" />
            Números Ganadores
          </h2>
          <div className="flex items-center gap-1.5">
            <Calendar size={14} className="text-gray-400" />
            <input
              type="date"
              value={fechaGanadores}
              onChange={(e) => setFechaGanadores(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        {loadingGanadores ? (
          <div className="flex justify-center py-4">
            <Loader2 size={18} className="animate-spin text-gray-400" />
          </div>
        ) : ganadores.length === 0 ? (
          <p className="text-center text-gray-400 text-sm py-3">
            No hay números ganadores registrados para esta fecha
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {ganadores.map(g => (
              <div key={g.id} className="flex flex-col items-center bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 min-w-[100px]">
                <span className="text-xs text-gray-500 truncate max-w-[90px] text-center">{g.sorteo_nombre}</span>
                <span className="font-bold text-lg font-mono text-blue-700">{formatNumGanador(g)}</span>
                {g.hora_cierre && (
                  <span className="text-xs text-gray-400">{g.hora_cierre.slice(0, 5)}</span>
                )}
                {typeof g.total_pagado_local === 'number' && (
                  <span className="text-xs text-gray-700 font-semibold mt-0.5">
                    Premio: ₡{Number(g.total_pagado_local).toLocaleString('es-CR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Búsqueda */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <form onSubmit={handleSubmit} className="flex gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={barcode}
              onChange={(e) => { setBarcode(e.target.value.toUpperCase()); setConsulta(null); setError(''); }}
              placeholder="Escanear o digitar codigo de ticket..."
              autoFocus
              className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
            />
          </div>
          <button
            type="submit"
            disabled={loading || !barcode.trim()}
            className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium flex items-center gap-1.5"
          >
            {loading && <Loader2 size={15} className="animate-spin" />}
            Buscar
          </button>
        </form>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            <AlertCircle size={15} className="shrink-0" />
            {error}
          </div>
        )}

        {/* Resultado de consulta */}
        {consulta && (
          <div className={`mt-3 border rounded-xl p-4 space-y-3 ${esGanador ? 'bg-yellow-50 border-yellow-300' : 'bg-gray-50 border-gray-200'}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono font-bold text-gray-900">{consulta.ticket_number}</p>
                <p className="text-xs text-gray-500">{consulta.sorteo_nombre}</p>
              </div>
              <span className="text-sm text-gray-600">
                Apostado: <b>₡{parseFloat(consulta.total).toLocaleString('es-CR')}</b>
              </span>
            </div>

            {consulta.anulado && (
              <div className="flex items-center gap-1.5 text-red-600 text-sm">
                <XCircle size={15} /> Ticket anulado
              </div>
            )}
            {consulta.ya_premiado && (
              <div className="flex items-center gap-1.5 text-orange-600 text-sm">
                <AlertCircle size={15} /> Este ticket ya fue pagado
              </div>
            )}
            {consulta.vencido && (
              <div className="flex items-center gap-1.5 text-red-600 text-sm">
                <XCircle size={15} /> Este tiquete ya está vencido
              </div>
            )}

            {esGanador && consulta.jugadas_ganadoras.length > 0 && (
              <div className="space-y-1">
                {consulta.jugadas_ganadoras.map((j, i) => (
                  <div key={i} className="flex justify-between text-sm bg-white rounded-lg px-3 py-1.5 border border-yellow-200">
                    <span className="font-mono text-gray-700">
                      {j.num1}{j.num2 ? `/${j.num2}` : ''} · {j.concepto}
                    </span>
                    <span className="font-bold text-green-700">
                      ₡{parseFloat(j.premio).toLocaleString('es-CR')}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {!consulta.vencido && !consulta.ya_premiado && !consulta.anulado && (
              <div className="flex justify-between items-center pt-1 border-t border-gray-200">
                <span className="font-semibold text-gray-700">Premio</span>
                <span className={`font-bold text-xl ${premioNum > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                  ₡{premioNum.toLocaleString('es-CR')}
                </span>
              </div>
            )}

            <div className="flex gap-2">
              {esGanador && (
                <button
                  onClick={pagar}
                  disabled={paying}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-semibold text-sm"
                >
                  {paying ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                  Pagar ₡{premioNum.toLocaleString('es-CR')}
                </button>
              )}
              <button
                onClick={() => { setConsulta(null); setBarcode(''); setError(''); setTimeout(() => inputRef.current?.focus(), 50); }}
                className="flex-1 py-2.5 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-100 text-sm"
              >
                {esGanador ? 'Cancelar' : 'Limpiar'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Comprobante último pago */}
      {comprobante && (
        <div className="bg-green-50 border border-green-300 rounded-xl p-4 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-green-800 flex items-center gap-2">
              <CheckCircle size={16} className="text-green-600" />
              Premio pagado
            </h3>
            <button
              onClick={() => setComprobante(null)}
              className="text-green-500 hover:text-green-700 text-xs"
            >
              Cerrar
            </button>
          </div>
          <div className="text-sm text-green-900 space-y-1 font-mono">
            <p><span className="text-green-600">Tiquete:</span> {comprobante.ticketNumber}</p>
            <p><span className="text-green-600">Sorteo:</span>  {comprobante.sorteoNombre}</p>
            <p className="text-lg font-bold">₡{comprobante.monto.toLocaleString('es-CR')}</p>
            <p><span className="text-green-600">Pagado por:</span> {comprobante.pagadoPor}</p>
            <p><span className="text-green-600">Hora:</span> {formatHoraCR(parseBackendDate(comprobante.fechaPago))}</p>
          </div>
          <button
            onClick={reimprimir}
            disabled={imprimiendo}
            className="w-full flex items-center justify-center gap-2 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
          >
            {imprimiendo
              ? <Loader2 size={14} className="animate-spin" />
              : <Printer size={14} />}
            Reimprimir comprobante
          </button>
        </div>
      )}
    </div>
  );
};

export default VendedorPremiosPage;
